/**
 * /cost-slo — Engine 25 · Cost SLO / Burn-Rate Guard.
 *
 * SRE error-budget discipline applied to spend: budgets are caps you notice
 * after breach; SLOs watch the burn RATE and predict exhaustion in advance.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gauge, RefreshCw, AlertCircle, Plus, Trash2, Flame } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}`, 'Content-Type': 'application/json' });

const glass = 'bg-slate-900/50 backdrop-blur-xl border border-white/10';

const STATUS = {
  healthy: { chip: 'bg-emerald-500/10 border-emerald-400/40 text-emerald-300', bar: 'bg-emerald-500/80' },
  warning: { chip: 'bg-amber-500/10 border-amber-400/40 text-amber-300', bar: 'bg-amber-500/80' },
  critical: { chip: 'bg-rose-500/10 border-rose-400/40 text-rose-300', bar: 'bg-rose-500/80' },
  exhausted: { chip: 'bg-rose-600/20 border-rose-500/60 text-rose-200', bar: 'bg-rose-600' },
};

const CostSlo = () => {
  const [slos, setSlos] = useState([]);
  const [check, setCheck] = useState(null);
  const [unauth, setUnauth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', feature_slug: '', monthly_budget_usd: '', degrade_action: 'alert' });
  const [formErr, setFormErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        fetch(`${API}/cost-slo/slos`, { headers: auth() }),
        fetch(`${API}/cost-slo/burn-check`, { headers: auth() }),
      ]);
      if (!s.ok || !c.ok) throw new Error('unauthorized');
      setSlos((await s.json()).slos);
      setCheck(await c.json());
      setUnauth(false);
    } catch (e) {
      setUnauth(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!form.name || !form.feature_slug || !form.monthly_budget_usd) { setFormErr('Name, feature slug and budget are required.'); return; }
    setFormErr('');
    const r = await fetch(`${API}/cost-slo/slos`, {
      method: 'POST', headers: auth(),
      body: JSON.stringify({ ...form, monthly_budget_usd: Number(form.monthly_budget_usd) }),
    });
    if (r.ok) { setForm({ name: '', feature_slug: '', monthly_budget_usd: '', degrade_action: 'alert' }); setShowForm(false); load(); }
    else setFormErr((await r.json())?.detail || 'Failed to create SLO');
  };

  const remove = async (id) => {
    await fetch(`${API}/cost-slo/slos/${id}`, { method: 'DELETE', headers: auth() });
    load();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="cost-slo-page">
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <Badge className="bg-emerald-500/10 border-emerald-400/40 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3" data-testid="cs-badge">
            <Gauge className="w-3 h-3 mr-1.5 inline" /> Engine 25 · Cost SLO / Burn-Rate Guard · world-first
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>Cost SLOs</BrandTitle></h1>
          <p className="text-slate-300 mt-2 max-w-2xl">
            Budgets are caps you notice <em>after</em> the breach. SLOs are control theory:
            define a $ budget per feature and we watch the <span className="text-emerald-300 font-semibold">burn rate</span> in
            real time — projected exhaustion date included, while there's still time to act.
          </p>
        </div>

        {unauth ? (
          <div className={`rounded-xl ${glass} px-6 py-12 text-center text-slate-500`} data-testid="cs-unauth">
            <AlertCircle className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            Sign in to define Cost SLOs. Try instantly with the <span className="text-emerald-400">Live Demo</span> button in the navbar.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[['SLOs defined', check?.total_slos, 'text-white', 'cs-stat-total'],
                ['Healthy', check?.healthy, 'text-emerald-300', 'cs-stat-healthy'],
                ['Warning', check?.warning, 'text-amber-300', 'cs-stat-warning'],
                ['Critical / exhausted', (check?.critical ?? 0) + (check?.exhausted ?? 0), 'text-rose-300', 'cs-stat-critical'],
              ].map(([label, value, color, tid]) => (
                <div key={tid} className={`rounded-xl ${glass} p-5`} data-testid={tid}>
                  <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">{label}</div>
                  <div className={`text-3xl font-extrabold tabular-nums font-mono ${color}`}>{value ?? '—'}</div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-white">Your SLOs</h2>
              <div className="flex gap-2">
                <button onClick={load} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 text-sm font-semibold" data-testid="cs-refresh">
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
                <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-950 px-3 py-1.5 text-sm font-bold" data-testid="cs-new-btn">
                  <Plus className="w-3.5 h-3.5" /> New SLO
                </button>
              </div>
            </div>

            {showForm && (
              <Card className={`${glass} border-emerald-400/20 mb-6`}>
                <CardContent className="p-5">
                  <div className="grid md:grid-cols-4 gap-3 mb-3">
                    <input className="rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 text-slate-100" placeholder="SLO name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cs-form-name" />
                    <input className="rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 text-slate-100" placeholder="feature_slug *" value={form.feature_slug} onChange={(e) => setForm({ ...form, feature_slug: e.target.value })} data-testid="cs-form-slug" />
                    <input type="number" className="rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 text-slate-100" placeholder="Monthly budget $ *" value={form.monthly_budget_usd} onChange={(e) => setForm({ ...form, monthly_budget_usd: e.target.value })} data-testid="cs-form-budget" />
                    <select className="rounded-lg border border-white/15 px-2 py-1.5 text-sm bg-slate-900 text-slate-100" value={form.degrade_action} onChange={(e) => setForm({ ...form, degrade_action: e.target.value })} data-testid="cs-form-action">
                      <option value="alert">On breach: alert</option>
                      <option value="degrade_model">On breach: degrade model</option>
                      <option value="hard_cap">On breach: hard cap</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={create} className="rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-950 px-4 py-1.5 text-sm font-bold" data-testid="cs-form-submit">Create SLO</button>
                    {formErr && <span className="text-xs text-rose-400" data-testid="cs-form-error">{formErr}</span>}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-4" data-testid="cs-slo-list">
              {slos.length === 0 ? (
                <div className={`rounded-xl ${glass} px-6 py-10 text-center text-slate-500`} data-testid="cs-empty">
                  <Gauge className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  No SLOs yet. Give your most expensive AI feature a budget with teeth.
                </div>
              ) : (
                slos.map((s) => {
                  const st = STATUS[s.burn.status] || STATUS.healthy;
                  const pct = Math.min(100, Math.round(100 * s.burn.mtd_spend_usd / s.monthly_budget_usd));
                  return (
                    <div key={s.id} className={`rounded-xl ${glass} p-5`} data-testid={`cs-slo-${s.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                        <div>
                          <div className="font-bold text-white">{s.name}</div>
                          <div className="text-xs font-mono text-slate-500">{s.feature_slug} · ${s.monthly_budget_usd.toLocaleString()}/mo budget</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md border ${st.chip}`} data-testid={`cs-status-${s.id}`}>{s.burn.status}</span>
                          <button onClick={() => remove(s.id)} className="text-slate-600 hover:text-rose-400" data-testid={`cs-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden mb-3">
                        <div className={`h-full ${st.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-slate-500 text-xs block">MTD spend</span><span className="font-mono font-bold text-white">${s.burn.mtd_spend_usd.toLocaleString()}</span></div>
                        <div><span className="text-slate-500 text-xs block">Burn rate</span><span className={`font-mono font-bold ${s.burn.burn_rate > 1 ? 'text-rose-300' : 'text-emerald-300'}`}>{s.burn.burn_rate}×</span></div>
                        <div><span className="text-slate-500 text-xs block">Projected month-end</span><span className="font-mono font-bold text-white">${s.burn.projected_month_end_usd.toLocaleString()}</span></div>
                        <div><span className="text-slate-500 text-xs block">Exhaustion</span><span className="font-mono font-bold text-amber-300">{s.burn.exhaustion_day_of_month ? `day ${Math.round(s.burn.exhaustion_day_of_month)}` : '—'}</span></div>
                      </div>
                      {s.burn.recommended_action && (
                        <div className="mt-3 text-xs text-rose-300 flex items-center gap-1.5" data-testid={`cs-action-${s.id}`}>
                          <Flame className="w-3.5 h-3.5" /> {s.burn.recommended_action}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        <Card className={`${glass} mt-10`}>
          <CardContent className="p-5">
            <h3 className="font-bold text-white mb-2">Budgets vs SLOs — the difference that saves you</h3>
            <p className="text-sm text-slate-200 leading-relaxed">
              A budget alert fires when the money is already gone. A burn-rate SLO fires when you're spending
              <span className="font-mono text-emerald-300"> 1.5×</span> faster than sustainable — typically weeks before
              the breach — and tells you the exact day the budget dies at the current rate. It's the SRE error-budget
              playbook, applied to dollars. Pair it with <Link to="/engine-27" className="text-emerald-400 hover:underline">the proxy's</Link> auto-degrade
              and overspend becomes a solved problem.
            </p>
            <p className="text-xs text-slate-500 mt-2 italic">First-to-market: Datadog/CloudZero ship threshold alerts; nobody ships cost burn-rate SLOs (Jun 2026).</p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CostSlo;
